import { useCallback, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Grid3X3,
  Layers,
  Loader2,
  Paintbrush2,
  ShoppingCart,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { getRouteState } from "@/lib/navigation";
import { createShopifyCart } from "@/lib/api";

const FLOOR_SHOPIFY_MAP = {
  vinyl: null,
  "anti-slip": null,
  wrap: null,
};

const SHOPIFY_DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN || "srbt1r-qn.myshopify.com";

const MATERIAL_RATES = {
  vinyl: { perSqm: 9, min: 12 },
  "anti-slip": { perSqm: 14, min: 18 },
  wrap: { perSqm: 18, min: 25 },
};

const QUANTITY_TIERS = [
  { min: 1,   max: 4,    mult: 1.0 },
  { min: 5,   max: 9,    mult: 0.92 },
  { min: 10,  max: 24,   mult: 0.85 },
  { min: 25,  max: 49,   mult: 0.78 },
  { min: 50,  max: 99,   mult: 0.72 },
  { min: 100, max: 9999, mult: 0.65 },
];

function calcFloorPrice({ widthM, heightM, materialId, quantity }) {
  const area = widthM * heightM;
  if (area <= 0) return null;
  const rate = MATERIAL_RATES[materialId] || MATERIAL_RATES.vinyl;
  const rawUnit = Math.max(rate.min, area * rate.perSqm);
  const tier = QUANTITY_TIERS.find((t) => quantity >= t.min && quantity <= t.max) || QUANTITY_TIERS[0];
  const unitPrice = rawUnit * tier.mult;
  return {
    unitPrice: Math.ceil(unitPrice * 100) / 100,
    totalPrice: Math.ceil(unitPrice * quantity * 100) / 100,
    area,
    perSqm: unitPrice / area,
    discount: Math.round((1 - tier.mult) * 100),
  };
}

function formatGBP(n) {
  if (n == null || isNaN(n)) return "";
  return `\u00a3${n.toFixed(2)}`;
}

const MATERIALS = [
  { id: "vinyl", title: "Vinyl Floor Sticker", short: "Indoor events & temporary display", description: "Removable calendared vinyl. Clear edges, full-colour print. Ideal for events, retail promotions, and seasonal floor graphics.", icon: Grid3X3 },
  { id: "anti-slip", title: "Anti-Slip Floor Print", short: "High-traffic, safety-certified", description: "Textured anti-slip laminate over a full-colour floor print. Meets UK slip-resistance requirements for retail floors, warehouses, and food-service areas.", icon: Layers },
  { id: "wrap", title: "Floor Wrap", short: "Full-area coverage, showrooms & exhibitions", description: "Large-format floor coverage for car showrooms, exhibition stands, and branded retail environments. Durable cast vinyl with matt or gloss finish.", icon: Paintbrush2 },
];

const FINISH_OPTIONS = [
  { id: "matte-lam", label: "Matte laminate", note: "Reduced glare, standard indoor", default: true },
  { id: "gloss-lam", label: "Gloss laminate", note: "Vibrant colours, indoor light traffic" },
  { id: "anti-slip", label: "Anti-slip texture", note: "Textured surface (auto for anti-slip material)" },
];

const PRESET_SIZES = [
  { label: "A3", w: "29.7", h: "42", unit: "cm", wM: 0.297, hM: 0.42, note: "Counter / door" },
  { label: "A2", w: "42", h: "59.4", unit: "cm", wM: 0.42, hM: 0.594, note: "Floor feature" },
  { label: "A1", w: "59.4", h: "84.1", unit: "cm", wM: 0.594, hM: 0.841, note: "Aisle / path" },
  { label: "A0", w: "84.1", h: "119", unit: "cm", wM: 0.841, hM: 1.19, note: "Large floor feature" },
  { label: "1 \u00d7 1 m", w: "1", h: "1", unit: "m", wM: 1, hM: 1, note: "Standard unit" },
  { label: "2 \u00d7 1 m", w: "2", h: "1", unit: "m", wM: 2, hM: 1, note: "Aisle runner" },
  { label: "3 \u00d7 2 m", w: "3", h: "2", unit: "m", wM: 3, hM: 2, note: "Showroom zone" },
];

const USE_CASES = [
  { id: "retail-promo", label: "Retail promotion", suggest: "vinyl" },
  { id: "event-wayfinding", label: "Event / wayfinding", suggest: "vinyl" },
  { id: "safety-zone", label: "Safety zones / hazards", suggest: "anti-slip" },
  { id: "showroom", label: "Car / trade showroom", suggest: "wrap" },
  { id: "outdoor", label: "Outdoor / pavement", suggest: "anti-slip" },
  { id: "unsure", label: "Not sure yet", suggest: null },
];

// ── Small presentational helpers (replace nested ternaries for readability) ──
const stepCircleCls = (done, active) => {
  if (done) return "bg-brand-gold text-brand-bg";
  if (active) return "border-2 border-brand-gold text-brand-gold bg-brand-gold/5";
  return "border border-white/20 text-brand-text-secondary/50";
};

const summarizeSize = (sizeLabel, dim) => {
  if (sizeLabel) return `Size: ${sizeLabel}.`;
  if (dim) return `Size: ${dim}.`;
  return "";
};

const finishOptionCls = (locked, isActive) => {
  if (locked) return "cursor-not-allowed border-white/[0.04] text-brand-text-secondary/25";
  if (isActive) return "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]";
  return "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]";
};

function StepBadge({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-3" data-testid={`floor-step-badge-${number}`}>
      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold transition-colors duration-300 ${stepCircleCls(done, active)}`}>
        {done ? <Check size={14} /> : number}
      </span>
      <span className={`text-[12px] tracking-[0.12em] uppercase font-semibold transition-colors duration-300 ${active || done ? "text-brand-text" : "text-brand-text-secondary/50"}`}>
        {label}
      </span>
    </div>
  );
}

function buildQuoteHref({ material, sizeLabel, width, height, sizeUnit, finishing, quantity }) {
  const params = new URLSearchParams();
  params.set("type", "Business Signage");
  const dim = width && height ? `${width}${sizeUnit} \u00d7 ${height}${sizeUnit}` : "";
  if (dim) params.set("dimensions", dim);
  if (quantity) params.set("quantity", String(quantity));
  const parts = [
    `Floor graphic: ${material?.title || "Floor Graphics"}.`,
    summarizeSize(sizeLabel, dim),
    `Finish: ${FINISH_OPTIONS.find((f) => f.id === finishing)?.label || "Matte laminate"}.`,
    quantity ? `Quantity: ${quantity}.` : "",
  ].filter(Boolean);
  params.set("description", parts.join(" "));
  return `/request-quote?${params.toString()}`;
}

export function FloorConfigurator() {
  const location = useLocation();
  const configuratorRef = useRef(null);

  const [sizeMode, setSizeMode] = useState("preset");
  const [presetIndex, setPresetIndex] = useState(null);
  const [customW, setCustomW] = useState("");
  const [customH, setCustomH] = useState("");
  const [customUnit, setCustomUnit] = useState("cm");
  const [materialId, setMaterialId] = useState("vinyl");
  const [finishing, setFinishing] = useState("matte-lam");
  const [quantity, setQuantity] = useState("1");
  const [useCase, setUseCase] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const material = useMemo(() => MATERIALS.find((m) => m.id === materialId) || MATERIALS[0], [materialId]);
  const suggestedMaterialId = useCase ? USE_CASES.find((u) => u.id === useCase)?.suggest : null;
  const activeFinish = materialId === "anti-slip" ? "anti-slip" : finishing;

  const sizeLabel = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null) return PRESET_SIZES[presetIndex]?.label || "";
    if (sizeMode === "custom" && customW && customH) return `${customW}${customUnit} \u00d7 ${customH}${customUnit}`;
    return "";
  }, [sizeMode, presetIndex, customW, customH, customUnit]);

  const sizeWidth = sizeMode === "preset" && presetIndex !== null ? PRESET_SIZES[presetIndex]?.w : customW;
  const sizeHeight = sizeMode === "preset" && presetIndex !== null ? PRESET_SIZES[presetIndex]?.h : customH;
  const sizeUnit = sizeMode === "preset" && presetIndex !== null ? (PRESET_SIZES[presetIndex]?.unit || "cm") : customUnit;

  const widthM = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null) return PRESET_SIZES[presetIndex]?.wM || 0;
    if (!customW) return 0;
    return customUnit === "m" ? parseFloat(customW) : parseFloat(customW) / 100;
  }, [sizeMode, presetIndex, customW, customUnit]);

  const heightM = useMemo(() => {
    if (sizeMode === "preset" && presetIndex !== null) return PRESET_SIZES[presetIndex]?.hM || 0;
    if (!customH) return 0;
    return customUnit === "m" ? parseFloat(customH) : parseFloat(customH) / 100;
  }, [sizeMode, presetIndex, customH, customUnit]);

  const hasSizeSelected = sizeMode === "preset" ? presetIndex !== null : (customW && customH);
  const qty = Math.max(1, parseInt(quantity, 10) || 1);

  const calc = useCallback((opts) => calcFloorPrice(opts), []);

  const livePrice = useMemo(() => {
    if (!hasSizeSelected || widthM <= 0 || heightM <= 0) return null;
    return calc({ widthM, heightM, materialId, quantity: qty });
  }, [hasSizeSelected, widthM, heightM, materialId, qty, calc]);

  const presetPrices = useMemo(() =>
    PRESET_SIZES.map((p) => calc({ widthM: p.wM, heightM: p.hM, materialId, quantity: qty })?.unitPrice || 0),
    [materialId, qty, calc]
  );

  const quoteHref = buildQuoteHref({ material, sizeLabel, width: sizeWidth, height: sizeHeight, sizeUnit, finishing: activeFinish, quantity });
  const shopifyProduct = FLOOR_SHOPIFY_MAP[materialId] || null;

  const handleFloorCheckout = async () => {
    if (!shopifyProduct || checkoutLoading) return;
    setCheckoutLoading(true);
    const note = [
      `Floor ${material.title}`, sizeLabel ? `Size: ${sizeLabel}` : null,
      `Finish: ${FINISH_OPTIONS.find((f) => f.id === activeFinish)?.label || "Matte laminate"}`,
      quantity !== "1" ? `Qty: ${quantity}` : null,
    ].filter(Boolean).join(" | ");
    try {
      const result = await createShopifyCart({ variantId: shopifyProduct.variantId, quantity: qty, handle: shopifyProduct.handle, note });
      window.open(result.checkout_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[FloorConfigurator] cart creation failed, using direct URL:", err);
      window.open(`https://${SHOPIFY_DOMAIN}/products/${shopifyProduct.handle}`, "_blank", "noopener,noreferrer");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleMaterialSelect = (matId) => {
    setMaterialId(matId);
    if (matId !== "anti-slip") {
      if (finishing === "anti-slip") setFinishing("matte-lam");
    }
  };

  return (
    <section ref={configuratorRef} className="mt-20" data-testid="floor-configurator-section">
      <div className="mb-4">
        <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold">Configure your floor graphic</p>
        <h2 className="mt-2 font-heading text-2xl sm:text-3xl tracking-tight text-brand-text">Three steps to your quote</h2>
      </div>

      <div className="flex items-center gap-4 sm:gap-6 mb-10 py-4 border-b border-white/[0.06] overflow-x-auto" data-testid="floor-steps-bar">
        <StepBadge number={1} label="Size" active={!hasSizeSelected} done={!!hasSizeSelected} />
        <ChevronRight size={14} className="text-white/20 shrink-0" />
        <StepBadge number={2} label="Material" active={!!hasSizeSelected} done={!!hasSizeSelected && !!materialId} />
        <ChevronRight size={14} className="text-white/20 shrink-0" />
        <StepBadge number={3} label="Finish" active={!!materialId} done={!!activeFinish} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
        {/* ── Left: Steps ── */}
        <div className="space-y-8">
          {/* STEP 1: Size */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="floor-step-1-size">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">1</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Choose a size</span>
            </div>
            <div className="flex gap-2 mb-6" data-testid="floor-size-mode-toggle">
              <button type="button" onClick={() => setSizeMode("preset")} className={`rounded-sm px-5 py-2.5 text-[13px] font-medium transition-all duration-200 ${sizeMode === "preset" ? "bg-brand-gold text-brand-bg" : "border border-white/15 text-brand-text-secondary/70 hover:text-brand-text hover:border-white/30"}`} data-testid="floor-size-mode-preset">Standard sizes</button>
              <button type="button" onClick={() => setSizeMode("custom")} className={`rounded-sm px-5 py-2.5 text-[13px] font-medium transition-all duration-200 ${sizeMode === "custom" ? "bg-brand-gold text-brand-bg" : "border border-white/15 text-brand-text-secondary/70 hover:text-brand-text hover:border-white/30"}`} data-testid="floor-size-mode-custom">Custom size</button>
            </div>
            {sizeMode === "preset" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="floor-preset-sizes-grid">
                {PRESET_SIZES.map((preset, i) => (
                  <button key={preset.label} type="button" onClick={() => setPresetIndex(i)} className={`rounded-sm border px-5 py-4 text-left transition-all duration-200 ${presetIndex === i ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]" : "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`floor-preset-size-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[15px] font-semibold ${presetIndex === i ? "text-brand-gold" : "text-brand-text"}`}>{preset.label}</span>
                      <span className={`text-[13px] font-semibold ${presetIndex === i ? "text-brand-gold/70" : "text-brand-text-secondary/40"}`}>{formatGBP(presetPrices[i])}</span>
                    </div>
                    <span className={`block text-[12px] mt-1 ${presetIndex === i ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{preset.note}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="rounded-sm border border-white/[0.08] bg-white/[0.015] p-5" data-testid="floor-custom-size-inputs">
                <div className="grid grid-cols-2 gap-4">
                  <label>
                    <span className="block text-[12px] font-medium tracking-wide text-brand-text-secondary/70 mb-2">Width</span>
                    <input type="number" min="0" inputMode="decimal" value={customW} onChange={(e) => setCustomW(e.target.value)} placeholder={customUnit === "m" ? "e.g. 2" : "e.g. 200"} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px] placeholder:text-brand-text-secondary/30" data-testid="floor-custom-width" />
                  </label>
                  <label>
                    <span className="block text-[12px] font-medium tracking-wide text-brand-text-secondary/70 mb-2">Height</span>
                    <input type="number" min="0" inputMode="decimal" value={customH} onChange={(e) => setCustomH(e.target.value)} placeholder={customUnit === "m" ? "e.g. 1" : "e.g. 100"} className="w-full bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-3 outline-none transition-colors text-[15px] placeholder:text-brand-text-secondary/30" data-testid="floor-custom-height" />
                  </label>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-[12px] text-brand-text-secondary/50">Unit:</span>
                  <div className="inline-flex rounded-sm border border-white/10 overflow-hidden" data-testid="floor-unit-toggle">
                    {[{ id: "cm", label: "cm" }, { id: "m", label: "metres" }].map((u) => (
                      <button key={u.id} type="button" onClick={() => setCustomUnit(u.id)} className={`px-4 py-2 text-[12px] font-medium transition-all ${customUnit === u.id ? "bg-brand-gold text-brand-bg" : "text-brand-text-secondary/60 hover:text-brand-text"}`} data-testid={`floor-unit-${u.id}`}>{u.label}</button>
                    ))}
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-brand-text-secondary/45">We'll confirm exact dimensions when we prepare your quote.</p>
              </div>
            )}
          </div>

          {/* STEP 2: Material */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="floor-step-2-material">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">2</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Pick a material</span>
            </div>
            <div className="mb-6" data-testid="floor-use-case-selector">
              <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Where is this for? <span className="font-normal text-brand-text-secondary/40">(optional)</span></p>
              <div className="flex flex-wrap gap-2">
                {USE_CASES.map((uc) => (
                  <button key={uc.id} type="button" onClick={() => { const next = uc.id === useCase ? null : uc.id; setUseCase(next); if (next && uc.suggest) handleMaterialSelect(uc.suggest); }} className={`rounded-full px-4 py-2 text-[12px] font-medium transition-all duration-200 ${useCase === uc.id ? "bg-brand-gold text-brand-bg" : "border border-white/12 text-brand-text-secondary/65 hover:text-brand-text hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`floor-use-case-${uc.id}`}>{uc.label}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="floor-material-cards">
              {MATERIALS.map((mat) => {
                const isActive = materialId === mat.id;
                const isSuggested = suggestedMaterialId === mat.id;
                return (
                  <button key={mat.id} type="button" onClick={() => handleMaterialSelect(mat.id)} className={`relative rounded-sm border p-5 text-left transition-all duration-200 ${isActive ? "border-brand-gold bg-brand-gold/10 ring-1 ring-brand-gold/30 shadow-[0_0_12px_rgba(203,160,82,0.08)]" : "border-white/10 bg-white/[0.01] hover:border-white/25 hover:bg-white/[0.03]"}`} data-testid={`floor-material-${mat.id}`}>
                    {isSuggested && !isActive && (
                      <span className="absolute -top-2.5 right-3 rounded-full bg-brand-gold/20 border border-brand-gold/30 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-gold">Suggested</span>
                    )}
                    <span className={isActive ? "text-brand-gold" : "text-brand-text-secondary/60"}><mat.icon size={20} /></span>
                    <p className={`mt-3 text-[15px] font-semibold ${isActive ? "text-brand-gold" : "text-brand-text"}`}>{mat.title}</p>
                    <p className={`mt-1 text-[12px] ${isActive ? "text-brand-gold/60" : "text-brand-text-secondary/50"}`}>{mat.short}</p>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-[12px] text-brand-text-secondary/50 leading-relaxed">{material.description}</p>
          </div>

          {/* STEP 3: Finish + Quantity */}
          <div className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-6 md:p-7" data-testid="floor-step-3-finish">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gold text-[13px] font-bold text-brand-bg">3</span>
              <span className="text-[15px] font-semibold text-brand-text tracking-tight">Finishing &amp; quantity</span>
            </div>
            <div className="mb-6">
              <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Edge finishing</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="floor-finish-options">
                {FINISH_OPTIONS.map((opt) => {
                  const locked = materialId === "anti-slip" && opt.id !== "anti-slip";
                  const isActive = activeFinish === opt.id;
                  return (
                    <button key={opt.id} type="button" disabled={locked} onClick={() => !locked && setFinishing(opt.id)} className={`rounded-sm border px-4 py-3.5 text-left transition-all duration-200 ${finishOptionCls(locked, isActive)}`} data-testid={`floor-finish-${opt.id}`}>
                      <span className={`block text-[13px] font-medium ${isActive ? "text-brand-gold" : "text-brand-text"}`}>{opt.label}</span>
                      <span className={`block text-[11px] mt-1 ${isActive ? "text-brand-gold/50" : "text-brand-text-secondary/40"}`}>{opt.note}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-[12px] font-medium text-brand-text-secondary/70 mb-3">Quantity</p>
              <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-28 bg-white/[0.04] border border-white/10 focus:border-brand-gold rounded-sm text-brand-text px-3.5 py-2.5 outline-none transition-colors text-[15px]" data-testid="floor-quantity-input" />
              <p className="mt-2 text-[11px] text-brand-text-secondary/40">5% off at 5+. 15% off at 10+. 22% off at 25+. 28% off at 50+. 35% off at 100+.</p>
            </div>
          </div>
        </div>

        {/* ── Right: Summary (sticky) ── */}
        <div className="lg:sticky lg:top-28" data-testid="floor-quote-summary">
          <div className="rounded-sm border border-brand-gold/20 bg-[linear-gradient(135deg,rgba(203,160,82,0.04),rgba(10,13,20,0.97))] p-6 md:p-7 shadow-[0_0_24px_rgba(203,160,82,0.04)]">
            <p className="text-[12px] tracking-[0.16em] uppercase font-bold text-brand-gold mb-6">Your Floor Graphic Summary</p>
            <div className="space-y-4">
              {[
                { label: "Size", value: sizeLabel || "Select a size above" },
                { label: "Material", value: material.title },
                { label: "Finish", value: FINISH_OPTIONS.find((f) => f.id === activeFinish)?.label || "Matte laminate" },
                ...(quantity && quantity !== "1" ? [{ label: "Qty", value: quantity }] : []),
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-4 border-b border-white/[0.06] pb-3 last:border-0 last:pb-0" data-testid={`floor-summary-${row.label.toLowerCase()}`}>
                  <span className="text-[12px] uppercase tracking-[0.12em] text-brand-text-secondary/55 font-medium">{row.label}</span>
                  <span className={`text-[14px] font-semibold text-right ${row.value === "Select a size above" ? "text-brand-text-secondary/30 font-normal" : "text-brand-text"}`}>{row.value}</span>
                </div>
              ))}
            </div>
            {livePrice && (
              <div className="mt-6 rounded-sm border border-brand-gold/15 bg-brand-gold/[0.04] p-4" data-testid="floor-live-price">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.12em] text-brand-text-secondary/50 font-medium">Estimated price</p>
                    <p className="mt-1 text-[28px] font-bold text-brand-gold leading-none">{formatGBP(livePrice.unitPrice)}</p>
                    {livePrice.area > 0 && (
                      <p className="mt-1 text-[12px] text-brand-text-secondary/45" data-testid="floor-price-per-sqm">{formatGBP(Math.round(livePrice.perSqm * 100) / 100)}/m\u00b2</p>
                    )}
                    {qty > 1 && (
                      <p className="mt-1 text-[12px] text-brand-text-secondary/50">{qty} units = <span className="text-brand-text font-semibold">{formatGBP(livePrice.unitPrice * qty)}</span></p>
                    )}
                  </div>
                  {livePrice.discount > 0 && (
                    <span className="rounded-full bg-green-500/15 border border-green-500/25 px-2.5 py-1 text-[10px] font-bold text-green-400 uppercase tracking-wider">{livePrice.discount}% off</span>
                  )}
                </div>
                <p className="mt-2 text-[10px] text-brand-text-secondary/35">Indicative. Final quote confirmed after artwork review.</p>
              </div>
            )}
            <Link to={quoteHref} state={getRouteState(location)} className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-sm bg-brand-gold px-7 py-4 text-[14px] font-bold tracking-wide text-brand-bg transition-all duration-300 hover:bg-brand-gold-hover hover:shadow-[0_0_20px_rgba(203,160,82,0.2)]" data-testid="floor-request-quote-cta">
              Request Floor Graphic Quote <ArrowRight size={16} />
            </Link>
            {shopifyProduct && (
              <button type="button" onClick={handleFloorCheckout} disabled={checkoutLoading} className="mt-3 flex w-full items-center justify-center gap-2.5 rounded-sm border border-white/15 px-7 py-3.5 text-[13px] font-medium tracking-wide text-brand-text-secondary transition-all duration-300 hover:border-brand-gold/40 hover:text-brand-text disabled:opacity-60" data-testid="floor-shopify-cta">
                {checkoutLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Preparing...</>
                  : <><ShoppingCart size={14} /> Order via Shopify</>
                }
              </button>
            )}
            <p className="mt-4 text-center text-[12px] text-brand-text-secondary/45" data-testid="floor-configurator-note">Not sure on every detail? Send what you know — we'll guide the rest.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
